import React, { useMemo, useRef, useState, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import JoditEditor from "jodit-react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Button,
  TextField, MenuItem, Stack, Box, Typography, Chip, Avatar, Tooltip,
  Menu, ListItemText, ListItemIcon, MenuItem as MenuOption, Divider,
  CircularProgress, Snackbar, Alert, useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  SupportAgent, Close, PhotoCamera, AttachFile, Storage, Delete,
  InsertDriveFile, Image as ImageIcon, Send, CheckCircle, FiberManualRecord,
  NorthEast, CropSquare, Square, Edit, TextFields, BlurOn, Undo, DeleteOutline, Crop, Add, Check,
} from "@mui/icons-material";

/**
 * HelpDeskDialog — Evora production component
 * Deps: @mui/material @mui/icons-material @emotion/react @emotion/styled jodit-react react-redux
 *
 * Screenshot flow: capture a real frame (getDisplayMedia, stream kept alive), hide the dialog,
 * drag a crosshair crop, annotate (arrow/box/pen/text/blur/redact), Save shot -> tray.
 * Nothing is attached until "Add N to ticket". "Take another" reuses the live stream (no re-prompt).
 *
 * Props: open, onClose, onSubmit(FormData)?, endpoint="/api/support/tickets", contextSources?
 */

const CATEGORIES = ["Bug", "Question", "Feature request", "Data / report issue", "Billing", "Access / permissions"];
const PRIORITIES = [{ value: "low", label: "Low", color: "#6B8E7E" }, { value: "medium", label: "Medium", color: "#C9A23B" }, { value: "high", label: "High", color: "#D77A33" }, { value: "urgent", label: "Urgent", color: "#B4493C" }];
const SWATCHES = ["#E5484D", "#F5A623", "#2E5E4E", "#2D7FF9", "#1B2620", "#FFFFFF"];
const WIDTHS = [{ k: "S", v: 3 }, { k: "M", v: 6 }, { k: "L", v: 10 }];
const SAGE = "#7FA493", FOREST = "#2E5E4E";

const fmtBytes = (b) => (b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1048576).toFixed(1)} MB`);
const uid = () => Math.random().toString(36).slice(2, 9);
const stripHtml = (h) => (h || "").replace(/<[^>]*>/g, "").trim();
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/* EDIT to match Evora's store shape. Each entry => { key, label, hint, value }. */
const DEFAULT_CONTEXT_SOURCES = (s) => [
  { key: "user", label: "Current user", value: s?.auth?.user, hint: s?.auth?.user?.email },
  { key: "facility", label: "Active facility", value: s?.facility?.current, hint: s?.facility?.current?.name },
  { key: "route", label: "Current screen", value: { pathname: window.location.pathname, hash: window.location.hash }, hint: window.location.pathname },
  { key: "batch", label: "Selected batch", value: s?.cultivation?.selectedBatch, hint: s?.cultivation?.selectedBatch?.id },
  { key: "app", label: "App version", value: { version: s?.app?.version, build: s?.app?.build, env: s?.app?.env }, hint: s?.app?.version && `v${s.app.version}` },
  { key: "errors", label: "Recent errors", value: s?.diagnostics?.recentErrors, hint: s?.diagnostics?.recentErrors?.length && `${s.diagnostics.recentErrors.length} captured` },
  { key: "session", label: "Session / device", value: { browser: navigator.userAgent, viewport: `${window.innerWidth}×${window.innerHeight}`, tz: Intl.DateTimeFormat().resolvedOptions().timeZone, time: new Date().toISOString() }, hint: `${window.innerWidth}×${window.innerHeight}` },
];

/* ---------- canvas helpers ---------- */
function drawArrow(ctx, p0, p1, color, w) {
  const ang = Math.atan2(p1.y - p0.y, p1.x - p0.x), head = 10 + w * 2.2;
  ctx.strokeStyle = color; ctx.fillStyle = color; ctx.lineWidth = w; ctx.lineCap = "round";
  ctx.beginPath(); ctx.moveTo(p0.x, p0.y); ctx.lineTo(p1.x, p1.y); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(p1.x, p1.y);
  ctx.lineTo(p1.x - head * Math.cos(ang - Math.PI / 7), p1.y - head * Math.sin(ang - Math.PI / 7));
  ctx.lineTo(p1.x - head * Math.cos(ang + Math.PI / 7), p1.y - head * Math.sin(ang + Math.PI / 7));
  ctx.closePath(); ctx.fill();
}
function pixelate(ctx, base, p0, p1) {
  const x = Math.min(p0.x, p1.x), y = Math.min(p0.y, p1.y), w = Math.abs(p1.x - p0.x), h = Math.abs(p1.y - p0.y);
  if (w < 2 || h < 2) return;
  const block = 9, tw = Math.max(1, Math.round(w / block)), th = Math.max(1, Math.round(h / block));
  const tmp = document.createElement("canvas"); tmp.width = tw; tmp.height = th;
  const tctx = tmp.getContext("2d"); tctx.imageSmoothingEnabled = false; tctx.drawImage(base, x, y, w, h, 0, 0, tw, th);
  ctx.imageSmoothingEnabled = false; ctx.drawImage(tmp, 0, 0, tw, th, x, y, w, h); ctx.imageSmoothingEnabled = true;
}
function rectOf(a, b) { return { x: Math.min(a.x, b.x), y: Math.min(a.y, b.y), w: Math.abs(b.x - a.x), h: Math.abs(b.y - a.y) }; }
function drawShape(ctx, base, sh) {
  const [a, b] = sh.points;
  if (sh.type === "pen") { ctx.strokeStyle = sh.color; ctx.lineWidth = sh.width; ctx.lineJoin = "round"; ctx.lineCap = "round"; ctx.beginPath(); sh.points.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y))); ctx.stroke(); }
  else if (sh.type === "rect" && b) { const r = rectOf(a, b); ctx.strokeStyle = sh.color; ctx.lineWidth = sh.width; ctx.strokeRect(r.x, r.y, r.w, r.h); }
  else if (sh.type === "arrow" && b) drawArrow(ctx, a, b, sh.color, sh.width);
  else if (sh.type === "blur" && b) pixelate(ctx, base, a, b);
  else if (sh.type === "redact" && b) { const r = rectOf(a, b); ctx.fillStyle = "#0B0B0B"; ctx.fillRect(r.x, r.y, r.w, r.h); }
  else if (sh.type === "text") { ctx.fillStyle = sh.color; ctx.font = `600 ${sh.size}px system-ui, sans-serif`; ctx.textBaseline = "top"; ctx.fillText(sh.text, a.x, a.y); }
}

/* ---------- Screenshot Studio ---------- */
function ScreenshotStudio({ source, hidden, onComplete, onRetake, onCancel }) {
  const [phase, setPhase] = useState("crop"); // crop | annotate | review
  const [scale, setScale] = useState(1);
  const [sel, setSel] = useState(null);
  const [cropped, setCropped] = useState(null);
  const [tray, setTray] = useState([]); // {blob,url}
  const dragRef = useRef(null);
  const stageRef = useRef(null);

  const [tool, setTool] = useState("arrow");
  const [color, setColor] = useState("#E5484D");
  const [width, setWidth] = useState(6);
  const [textBox, setTextBox] = useState(null);
  const canvasRef = useRef(null);
  const shapesRef = useRef([]);
  const draftRef = useRef(null);
  const [, force] = useState(0); const bump = () => force((n) => n + 1);

  const dataUrl = useMemo(() => (source ? source.toDataURL() : null), [source]);

  useEffect(() => {
    if (!source) { setTray([]); setPhase("crop"); return; }
    setPhase("crop"); setSel(null); setCropped(null); shapesRef.current = []; draftRef.current = null;
    setScale(Math.min((window.innerWidth * 0.92) / source.width, (window.innerHeight * 0.7) / source.height, 1));
  }, [source]);

  const dispW = source ? source.width * scale : 0, dispH = source ? source.height * scale : 0;
  const cropPt = (e) => { const r = stageRef.current.getBoundingClientRect(); return { x: Math.max(0, Math.min(dispW, e.clientX - r.left)), y: Math.max(0, Math.min(dispH, e.clientY - r.top)) }; };
  const onCropDown = (e) => { e.currentTarget.setPointerCapture(e.pointerId); const p = cropPt(e); dragRef.current = p; setSel({ x: p.x, y: p.y, w: 0, h: 0 }); };
  const onCropMove = (e) => { if (!dragRef.current) return; const s = dragRef.current, c = cropPt(e); setSel({ x: Math.min(s.x, c.x), y: Math.min(s.y, c.y), w: Math.abs(c.x - s.x), h: Math.abs(c.y - s.y) }); };
  const onCropUp = () => { dragRef.current = null; };
  const doCrop = (full) => {
    const reg = full ? { x: 0, y: 0, w: dispW, h: dispH } : sel;
    if (!reg || reg.w < 5 || reg.h < 5) return;
    const sx = reg.x / scale, sy = reg.y / scale, sw = reg.w / scale, sh = reg.h / scale;
    const c = document.createElement("canvas"); c.width = sw; c.height = sh; c.getContext("2d").drawImage(source, sx, sy, sw, sh, 0, 0, sw, sh);
    setCropped(c); setPhase("annotate");
  };

  const redraw = useCallback(() => {
    const cv = canvasRef.current; if (!cv || !cropped) return;
    const ctx = cv.getContext("2d"); ctx.clearRect(0, 0, cv.width, cv.height); ctx.drawImage(cropped, 0, 0);
    shapesRef.current.forEach((s) => drawShape(ctx, cropped, s));
    if (draftRef.current) drawShape(ctx, cropped, draftRef.current);
  }, [cropped]);
  useEffect(() => { if (phase !== "annotate" || !cropped) return; const cv = canvasRef.current; cv.width = cropped.width; cv.height = cropped.height; redraw(); }, [phase, cropped, redraw]);

  const annScale = cropped ? Math.min((window.innerWidth * 0.9) / cropped.width, (window.innerHeight * 0.58) / cropped.height, 1) : 1;
  const annPt = (e) => { const cv = canvasRef.current, r = cv.getBoundingClientRect(); return { x: (e.clientX - r.left) * (cv.width / r.width), y: (e.clientY - r.top) * (cv.height / r.height) }; };
  const onAnnDown = (e) => {
    if (tool === "text") { const p = annPt(e); setTextBox({ cx: p.x, cy: p.y, sx: e.clientX, sy: e.clientY, val: "" }); return; }
    e.currentTarget.setPointerCapture(e.pointerId);
    const p = annPt(e); draftRef.current = { type: tool, color, width, size: width * 4 + 12, points: tool === "pen" ? [p] : [p, p] };
  };
  const onAnnMove = (e) => { if (!draftRef.current) return; const p = annPt(e); const d = draftRef.current; if (d.type === "pen") d.points.push(p); else d.points[1] = p; redraw(); };
  const onAnnUp = () => { const d = draftRef.current; if (d) { const [a, b] = d.points; if (d.type === "pen" || (b && (Math.abs(b.x - a.x) > 3 || Math.abs(b.y - a.y) > 3))) shapesRef.current.push(d); } draftRef.current = null; redraw(); bump(); };
  const commitText = () => { if (textBox && textBox.val.trim()) { shapesRef.current.push({ type: "text", color, size: width * 4 + 12, points: [{ x: textBox.cx, y: textBox.cy }], text: textBox.val }); redraw(); bump(); } setTextBox(null); };
  const undo = () => { shapesRef.current.pop(); redraw(); bump(); };
  const clearAll = () => { shapesRef.current = []; redraw(); bump(); };

  const saveShot = () => { const url = canvasRef.current.toDataURL("image/png"); canvasRef.current.toBlob((blob) => { setTray((t) => [...t, { blob, url }]); setPhase("review"); }, "image/png"); };
  const discardShot = () => (tray.length ? setPhase("review") : onCancel());
  const removeTray = (i) => setTray((t) => t.filter((_, j) => j !== i));

  const TOOLS = [
    { k: "arrow", icon: <NorthEast fontSize="small" />, t: "Arrow" },
    { k: "rect", icon: <CropSquare fontSize="small" />, t: "Box" },
    { k: "pen", icon: <Edit fontSize="small" />, t: "Pen" },
    { k: "text", icon: <TextFields fontSize="small" />, t: "Text" },
    { k: "blur", icon: <BlurOn fontSize="small" />, t: "Blur (pixelate)" },
    { k: "redact", icon: <Square fontSize="small" />, t: "Black-box redact" },
  ];

  if (!source) return null;
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1500, background: "rgba(12,18,15,.92)", display: hidden ? "none" : "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", userSelect: "none" }}>
      {phase === "crop" && (
        <>
          <div style={{ color: "#cfe0d7", fontSize: 13.5, marginBottom: 12, fontFamily: "system-ui" }}>Drag to select an area — or capture the whole frame.</div>
          <div ref={stageRef} onPointerDown={onCropDown} onPointerMove={onCropMove} onPointerUp={onCropUp} style={{ position: "relative", width: dispW, height: dispH, cursor: "crosshair", boxShadow: "0 10px 40px rgba(0,0,0,.5)" }}>
            <img src={dataUrl} alt="" draggable={false} style={{ width: dispW, height: dispH, display: "block", pointerEvents: "none" }} />
            {sel && sel.w > 0 && (<>
              <div style={{ position: "absolute", left: 0, top: 0, width: dispW, height: sel.y, background: "rgba(12,18,15,.6)" }} />
              <div style={{ position: "absolute", left: 0, top: sel.y + sel.h, width: dispW, height: dispH - sel.y - sel.h, background: "rgba(12,18,15,.6)" }} />
              <div style={{ position: "absolute", left: 0, top: sel.y, width: sel.x, height: sel.h, background: "rgba(12,18,15,.6)" }} />
              <div style={{ position: "absolute", left: sel.x + sel.w, top: sel.y, width: dispW - sel.x - sel.w, height: sel.h, background: "rgba(12,18,15,.6)" }} />
              <div style={{ position: "absolute", left: sel.x, top: sel.y, width: sel.w, height: sel.h, border: `2px solid ${SAGE}`, boxSizing: "border-box" }} />
              <div style={{ position: "absolute", left: sel.x, top: Math.max(0, sel.y - 22), background: FOREST, color: "#fff", fontSize: 11, padding: "2px 6px", borderRadius: 4, fontFamily: "system-ui" }}>{Math.round(sel.w / scale)} × {Math.round(sel.h / scale)}</div>
            </>)}
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <SBtn onClick={discardShot} ghost>{tray.length ? "Back" : "Cancel"}</SBtn>
            <SBtn onClick={() => doCrop(true)} ghost>Whole frame</SBtn>
            <SBtn onClick={() => doCrop(false)} disabled={!sel || sel.w < 5}><Crop fontSize="small" /> Crop</SBtn>
          </div>
        </>
      )}

      {phase === "annotate" && (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 6, padding: 8, background: "#16201b", borderRadius: 12, marginBottom: 12, flexWrap: "wrap", justifyContent: "center", maxWidth: "94vw" }}>
            {TOOLS.map((tl) => (<button key={tl.k} title={tl.t} onClick={() => setTool(tl.k)} style={{ width: 36, height: 36, borderRadius: 8, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: tool === tl.k ? "#16201b" : "#cfe0d7", background: tool === tl.k ? SAGE : "transparent" }}>{tl.icon}</button>))}
            <div style={{ width: 1, height: 24, background: "#2c3a32", margin: "0 4px" }} />
            {SWATCHES.map((s) => (<button key={s} title={s} onClick={() => setColor(s)} style={{ width: 22, height: 22, borderRadius: "50%", cursor: "pointer", background: s, border: color === s ? "2px solid #fff" : "2px solid rgba(255,255,255,.25)" }} />))}
            <div style={{ width: 1, height: 24, background: "#2c3a32", margin: "0 4px" }} />
            {WIDTHS.map((w) => (<button key={w.k} title={`Stroke ${w.k}`} onClick={() => setWidth(w.v)} style={{ width: 30, height: 30, borderRadius: 8, border: "none", cursor: "pointer", color: width === w.v ? "#16201b" : "#cfe0d7", background: width === w.v ? SAGE : "transparent", fontWeight: 700, fontSize: 12 }}>{w.k}</button>))}
            <div style={{ width: 1, height: 24, background: "#2c3a32", margin: "0 4px" }} />
            <button title="Undo" onClick={undo} style={{ width: 34, height: 34, borderRadius: 8, border: "none", cursor: "pointer", color: "#cfe0d7", background: "transparent" }}><Undo fontSize="small" /></button>
            <button title="Clear" onClick={clearAll} style={{ width: 34, height: 34, borderRadius: 8, border: "none", cursor: "pointer", color: "#cfe0d7", background: "transparent" }}><DeleteOutline fontSize="small" /></button>
          </div>
          <div style={{ position: "relative", boxShadow: "0 10px 40px rgba(0,0,0,.5)" }}>
            <canvas ref={canvasRef} onPointerDown={onAnnDown} onPointerMove={onAnnMove} onPointerUp={onAnnUp} style={{ width: cropped ? cropped.width * annScale : 0, height: cropped ? cropped.height * annScale : 0, display: "block", cursor: tool === "text" ? "text" : "crosshair", touchAction: "none" }} />
            {textBox && (<input autoFocus value={textBox.val} onChange={(e) => setTextBox({ ...textBox, val: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter") commitText(); if (e.key === "Escape") setTextBox(null); }} onBlur={commitText} placeholder="Type, then Enter" style={{ position: "fixed", left: textBox.sx, top: textBox.sy, font: `600 ${(width * 4 + 12) * annScale}px system-ui`, color, background: "rgba(255,255,255,.95)", border: `1px solid ${SAGE}`, borderRadius: 4, padding: "1px 4px", outline: "none", zIndex: 1600 }} />)}
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap", justifyContent: "center" }}>
            <SBtn onClick={() => setPhase("crop")} ghost>Re-crop</SBtn>
            <SBtn onClick={discardShot} ghost>Discard shot</SBtn>
            <SBtn onClick={saveShot}><Check fontSize="small" /> Save shot</SBtn>
          </div>
        </>
      )}

      {phase === "review" && (
        <div style={{ width: "min(680px, 92vw)" }}>
          <div style={{ color: "#cfe0d7", fontSize: 14, fontWeight: 600, marginBottom: 12, fontFamily: "system-ui" }}>Captured shots ({tray.length})</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            {tray.map((s, i) => (
              <div key={i} style={{ position: "relative", width: 150, borderRadius: 10, overflow: "hidden", border: "1px solid #2c3a32" }}>
                <img src={s.url} alt="" style={{ width: "100%", height: 96, objectFit: "cover", display: "block" }} />
                <button onClick={() => removeTray(i)} title="Remove" style={{ position: "absolute", top: 6, right: 6, width: 24, height: 24, borderRadius: 6, border: "none", background: "rgba(0,0,0,.6)", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><Delete sx={{ fontSize: 14 }} /></button>
              </div>
            ))}
            <button onClick={onRetake} style={{ width: 150, height: 96, borderRadius: 10, border: "1.5px dashed #3a4a40", background: "transparent", color: "#cfe0d7", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, fontFamily: "system-ui", fontSize: 13 }}><Add /> Take another</button>
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "flex-end" }}>
            <SBtn onClick={onCancel} ghost>Cancel all</SBtn>
            <SBtn onClick={() => onComplete(tray)} disabled={!tray.length}><Check fontSize="small" /> Add {tray.length} to ticket</SBtn>
          </div>
        </div>
      )}
    </div>
  );
}
function SBtn({ children, onClick, ghost, disabled }) {
  return (<button onClick={onClick} disabled={disabled} style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "9px 16px", borderRadius: 9, fontWeight: 600, fontSize: 13.5, fontFamily: "system-ui", cursor: disabled ? "default" : "pointer", border: ghost ? "1px solid #3a4a40" : "none", color: ghost ? "#cfe0d7" : "#fff", background: ghost ? "transparent" : (disabled ? "#3a4a40" : FOREST), opacity: disabled ? 0.5 : 1 }}>{children}</button>);
}

/* ---------- form body (reusable: standalone dialog OR embedded reader pane) ---------- */
export function HelpDeskForm({
  endpoint = "/api/support/tickets",
  onSubmit,
  contextSources,
  onCaptureActiveChange,
  onSubmitted,
  onCancel,
  showHeader = false,
  showCancel = true,
}) {
  const editorRef = useRef(null);
  const fileRef = useRef(null);
  const streamRef = useRef(null);

  const storeState = useSelector((s) => s);
  const sources = useMemo(() => (contextSources || DEFAULT_CONTEXT_SOURCES(storeState)).filter((x) => x.value != null), [contextSources, storeState]);

  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [priority, setPriority] = useState("medium");
  const [description, setDescription] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [ctxAnchor, setCtxAnchor] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [studioSrc, setStudioSrc] = useState(null);
  const [hidden, setHidden] = useState(false);

  const joditConfig = useMemo(() => ({ readonly: false, height: 200, placeholder: "Describe what happened, what you expected, and steps to reproduce…", toolbarAdaptive: false, buttons: ["bold", "italic", "underline", "|", "ul", "ol", "|", "link", "|", "undo", "redo"], statusbar: false }), []);

  const reset = useCallback(() => { setSubject(""); setCategory(CATEGORIES[0]); setPriority("medium"); setDescription(""); setAttachments([]); }, []);
  const stopStream = useCallback(() => { streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null; }, []);
  const cleanup = useCallback(() => { stopStream(); setStudioSrc(null); }, [stopStream]);
  const handleCancel = () => { cleanup(); onCancel?.(); };
  useEffect(() => () => stopStream(), [stopStream]);

  // Tell the surrounding surface (standalone Dialog or the embedding Help Center
  // Dialog) to hide itself while we grab/annotate a screenshot — otherwise the
  // capture would include the dialog. Mirrors the old inline `hidden || studioSrc`.
  useEffect(() => { onCaptureActiveChange?.(hidden || Boolean(studioSrc)); }, [hidden, studioSrc, onCaptureActiveChange]);

  const grabFrame = useCallback(async () => {
    if (!navigator.mediaDevices?.getDisplayMedia) throw Object.assign(new Error("unsupported"), { name: "Unsupported" });
    if (!streamRef.current) {
      streamRef.current = await navigator.mediaDevices.getDisplayMedia({ video: { displaySurface: "browser" }, preferCurrentTab: true, audio: false });
      streamRef.current.getVideoTracks()[0].addEventListener("ended", () => { streamRef.current = null; });
    }
    const video = document.createElement("video"); video.srcObject = streamRef.current; await video.play();
    await sleep(120);
    const c = document.createElement("canvas"); c.width = video.videoWidth; c.height = video.videoHeight;
    c.getContext("2d").drawImage(video, 0, 0); video.pause(); video.srcObject = null;
    return c;
  }, []);

  // used for the first Screenshot click AND "Take another" (reuses the live stream)
  const capture = useCallback(async () => {
    setHidden(true); await sleep(220);
    try { const frame = await grabFrame(); setStudioSrc(frame); }
    catch (err) { if (err?.name !== "NotAllowedError") setToast({ sev: err?.name === "Unsupported" ? "warning" : "error", msg: err?.name === "Unsupported" ? "Screen capture isn’t supported in this browser." : "Couldn’t capture the screen." }); }
    finally { setHidden(false); }
  }, [grabFrame]);

  const completeShots = (frames) => {
    setAttachments((a) => [...a, ...frames.map((f) => ({ id: uid(), kind: "image", name: `screenshot-${Date.now()}-${uid()}.png`, meta: `PNG · ${fmtBytes(f.blob.size)}`, blob: f.blob, preview: URL.createObjectURL(f.blob) }))]);
    setStudioSrc(null);
  };

  const onFiles = (e) => { const files = Array.from(e.target.files || []); setAttachments((a) => [...a, ...files.map((f) => ({ id: uid(), kind: f.type.startsWith("image/") ? "image" : "file", name: f.name, meta: `${(f.type || "file").split("/").pop().toUpperCase()} · ${fmtBytes(f.size)}`, file: f, preview: f.type.startsWith("image/") ? URL.createObjectURL(f) : null }))]); e.target.value = ""; };
  const attachContext = (src) => { setAttachments((a) => a.some((x) => x.ctxKey === src.key) ? a : [...a, { id: uid(), kind: "context", ctxKey: src.key, name: `${src.key}.json`, meta: `App data · ${fmtBytes(new Blob([JSON.stringify(src.value)]).size)}`, json: JSON.stringify(src.value, null, 2) }]); setCtxAnchor(null); };
  const removeAtt = (id) => setAttachments((a) => { const t = a.find((x) => x.id === id); if (t?.preview) URL.revokeObjectURL(t.preview); return a.filter((x) => x.id !== id); });
  const canSubmit = subject.trim() && stripHtml(description);

  const handleSubmit = async () => {
    setSubmitting(true);
    const fd = new FormData();
    fd.append("subject", subject.trim()); fd.append("category", category); fd.append("priority", priority); fd.append("description", description);
    attachments.forEach((a) => { if (a.file) fd.append("files", a.file, a.name); else if (a.blob) fd.append("files", a.blob, a.name); else if (a.json != null) fd.append("context", new Blob([a.json], { type: "application/json" }), a.name); });
    try {
      let issueKey = null;
      if (onSubmit) { const r = await onSubmit(fd); issueKey = r?.issueKey || null; }
      else {
        const res = await fetch(endpoint, { method: "POST", body: fd });
        if (!res.ok) throw new Error(res.statusText);
        const data = await res.json().catch(() => null);
        issueKey = data?.issueKey || null;
      }
      setToast({ sev: "success", msg: issueKey ? `Ticket ${issueKey} submitted — we’ll follow up by email.` : "Ticket submitted — a copy is on its way to your inbox." });
      reset(); cleanup(); onSubmitted?.(issueKey);
    } catch { setToast({ sev: "error", msg: "Couldn’t submit the ticket. Please try again." }); }
    finally { setSubmitting(false); }
  };

  const prio = PRIORITIES.find((p) => p.value === priority);

  return (
    <>
      {showHeader && (
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pr: 6 }}>
          <Avatar variant="rounded" sx={{ bgcolor: "#EAF1ED", color: FOREST, width: 36, height: 36 }}><SupportAgent fontSize="small" /></Avatar>
          <Box><Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>New support ticket</Typography><Typography variant="caption" color="text.secondary">We usually reply within a few hours.</Typography></Box>
          <IconButton onClick={handleCancel} sx={{ position: "absolute", right: 8, top: 8 }}><Close /></IconButton>
        </DialogTitle>
      )}

      <DialogContent dividers>
          <Stack spacing={2}>
            <TextField label="Subject" required fullWidth size="small" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Short summary of the issue" />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField select label="Category" fullWidth size="small" value={category} onChange={(e) => setCategory(e.target.value)}>{CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}</TextField>
              <TextField select label="Priority" fullWidth size="small" value={priority} onChange={(e) => setPriority(e.target.value)}>{PRIORITIES.map((p) => (<MenuItem key={p.value} value={p.value}><Stack direction="row" alignItems="center" spacing={1}><FiberManualRecord sx={{ fontSize: 12, color: p.color }} /><span>{p.label}</span></Stack></MenuItem>))}</TextField>
            </Stack>

            <Box>
              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: "block", mb: 0.5 }}>Description *</Typography>
              <Box sx={{ "& .jodit-container": { borderRadius: 1.5, borderColor: "#DCE5E0" } }}><JoditEditor ref={editorRef} value={description} config={joditConfig} onBlur={(html) => setDescription(html)} /></Box>
            </Box>

            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              <Button variant="outlined" size="small" startIcon={<PhotoCamera />} onClick={capture} sx={{ color: "#234B3E", borderColor: "#DCE5E0", "&:hover": { borderColor: FOREST, bgcolor: "#EAF1ED" } }}>Screenshot</Button>
              <Button variant="outlined" size="small" startIcon={<AttachFile />} onClick={() => fileRef.current?.click()} sx={{ color: "#234B3E", borderColor: "#DCE5E0", "&:hover": { borderColor: FOREST, bgcolor: "#EAF1ED" } }}>Attach file</Button>
              <Button variant="outlined" size="small" startIcon={<Storage />} onClick={(e) => setCtxAnchor(e.currentTarget)} sx={{ color: "#234B3E", borderColor: "#DCE5E0", "&:hover": { borderColor: FOREST, bgcolor: "#EAF1ED" } }}>Attach app data</Button>
              <input ref={fileRef} type="file" multiple hidden onChange={onFiles} />
            </Stack>

            <Menu anchorEl={ctxAnchor} open={Boolean(ctxAnchor)} onClose={() => setCtxAnchor(null)}>
              <Typography variant="overline" color="text.secondary" sx={{ px: 2, py: 0.5 }}>Attach from app state</Typography>
              {sources.map((s) => { const added = attachments.some((a) => a.ctxKey === s.key); return (<MenuOption key={s.key} onClick={() => attachContext(s)} disabled={added}><ListItemIcon><Storage fontSize="small" sx={{ color: SAGE }} /></ListItemIcon><ListItemText primary={s.label} secondary={s.hint || undefined} />{added && <CheckCircle fontSize="small" sx={{ color: FOREST, ml: 1 }} />}</MenuOption>); })}
              <Divider />
              <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 0.75, display: "block" }}>App data is sent as JSON with your ticket.</Typography>
            </Menu>

            {attachments.length > 0 && (
              <Box>
                <Typography variant="caption" fontWeight={600} color="text.secondary">Attachments ({attachments.length})</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                  {attachments.map((a) => (
                    <Box key={a.id} sx={{ width: 132, border: "1px solid #DCE5E0", borderRadius: 1.5, overflow: "hidden", position: "relative" }}>
                      <Box sx={{ height: 74, bgcolor: "#EAF1ED", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>{a.kind === "image" && a.preview ? <Box component="img" src={a.preview} alt={a.name} sx={{ width: "100%", height: "100%", objectFit: "cover" }} /> : a.kind === "context" ? <Storage sx={{ color: SAGE, fontSize: 26 }} /> : <InsertDriveFile sx={{ color: SAGE, fontSize: 26 }} />}</Box>
                      <Box sx={{ p: 1 }}><Typography variant="caption" fontWeight={600} noWrap display="block" title={a.name}>{a.name}</Typography><Typography variant="caption" color="text.secondary">{a.meta}</Typography></Box>
                      <Tooltip title="Remove"><IconButton size="small" onClick={() => removeAtt(a.id)} sx={{ position: "absolute", top: 4, right: 4, bgcolor: "rgba(27,38,32,.72)", color: "#fff", "&:hover": { bgcolor: "rgba(27,38,32,.9)" } }}><Delete sx={{ fontSize: 14 }} /></IconButton></Tooltip>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "space-between", px: 3, py: 1.5 }}>
          <Chip size="small" icon={<FiberManualRecord sx={{ fontSize: 12, color: `${prio.color} !important` }} />} label={`${prio.label} priority`} sx={{ bgcolor: "transparent", color: prio.color, fontWeight: 600 }} />
          <Stack direction="row" spacing={1}>
            {showCancel && <Button onClick={handleCancel} color="inherit">Cancel</Button>}
            <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit || submitting} startIcon={submitting ? <CircularProgress size={15} color="inherit" /> : <Send />} sx={{ bgcolor: FOREST, "&:hover": { bgcolor: "#234B3E" } }}>{submitting ? "Submitting…" : "Submit ticket"}</Button>
          </Stack>
        </DialogActions>

      {/* Portaled to body so the parent dialog's visibility:hidden (during capture)
          never reaches them — single code path for standalone and embedded use. */}
      {createPortal(
        <ScreenshotStudio source={studioSrc} hidden={hidden} onComplete={completeShots} onRetake={capture} onCancel={() => setStudioSrc(null)} />,
        document.body
      )}
      {createPortal(
        <Snackbar open={Boolean(toast)} autoHideDuration={5000} onClose={() => setToast(null)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>{toast ? <Alert severity={toast.sev} variant="filled" onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}</Snackbar>,
        document.body
      )}
    </>
  );
}

/* ---------- standalone dialog (back-compatible default export) ---------- */
export default function HelpDeskDialog({ open, onClose, onSubmit, endpoint = "/api/support/tickets", contextSources }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [surfaceHidden, setSurfaceHidden] = useState(false);
  return (
    <Dialog open={open} onClose={onClose} fullScreen={fullScreen} fullWidth maxWidth="sm" sx={{ visibility: surfaceHidden ? "hidden" : "visible" }} slotProps={{ paper: { sx: { borderRadius: fullScreen ? 0 : 2.5 } } }}>
      <HelpDeskForm
        showHeader
        showCancel
        endpoint={endpoint}
        onSubmit={onSubmit}
        contextSources={contextSources}
        onCaptureActiveChange={setSurfaceHidden}
        onCancel={onClose}
        onSubmitted={onClose}
      />
    </Dialog>
  );
}
